import * as React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Map,
  List,
  CalendarDays,
  PlusCircle,
  LogIn,
  LogOut,
  User,
  Menu,
  Home,
  ChevronDown,
  ChevronRight,
  Layers,
  Waves,
  Zap,
  Car,
  TreePine,
  Shield,
  Footprints,
  ShieldCheck,
  Route,
  Building2,
  MapPin,
} from "lucide-react";
import { getAllBarrioSlugs } from "./utils/barrios";

import { useAuth } from "./AuthContext";
import AppRouter from "./router";
import { cn } from "./lib/utils";
import { Button } from "./components/ui/button";
import { Separator } from "./components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./components/ui/sheet";

// Instagram SVG icon (not in lucide-react)
const InstagramIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

// ─── Types ──────────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  to?: string;
  icon: React.ElementType;
  onClick?: () => void;
  children?: { label: string; to: string; icon: React.ElementType }[];
}

// ─── Constants ──────────────────────────────────────────────────────────────

const barrioItems = getAllBarrioSlugs().map(({ slug, nombre }) => ({
  label: nombre,
  to: `/barrio/${slug}`,
  icon: MapPin,
}));

const tematicaItems = [
  { label: "Trampolines", to: "/tematica/trampolines/1", icon: Zap },
  { label: "Autos", to: "/tematica/Autos/2", icon: Car },
  { label: "Plazas temáticas", to: "/tematica/plazas/3", icon: TreePine },
  { label: "Escape Rooms", to: "/tematica/escaperooms/4", icon: Shield },
  { label: "Fútbol", to: "/tematica/futbol/5", icon: Footprints },
  { label: "Parques acuáticos", to: "/tematica/parquesacuaticos/6", icon: Waves },
];

const navItems: NavItem[] = [
  { label: "Inicio", to: "/", icon: Home },
  { label: "Mapa", to: "/", icon: Map },
  { label: "Lista de lugares", to: "/lista", icon: List },
  { label: "Eventos", to: "/eventos", icon: CalendarDays },
  { label: "Plan de salida", to: "/plan-salida", icon: Route },
  { label: "Agregar lugar", to: "/create", icon: PlusCircle },
  {
    label: "Por Temática",
    icon: Layers,
    children: tematicaItems,
  },
];

const SIDEBAR_WIDTH = 256;

// ─── NavLink ─────────────────────────────────────────────────────────────────

function NavLink({
  item,
  isActive,
  onClick,
  indent = false,
}: {
  item: { label: string; to?: string; icon: React.ElementType; onClick?: () => void };
  isActive?: boolean;
  onClick?: () => void;
  indent?: boolean;
}) {
  const Icon = item.icon;
  const base = cn(
    "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 select-none",
    indent && "pl-9",
    isActive
      ? "bg-white/20 text-white"
      : "text-white/75 hover:bg-white/10 hover:text-white"
  );

  if (item.to) {
    return (
      <Link to={item.to} className={base} onClick={onClick}>
        <Icon className="size-4 shrink-0" />
        {item.label}
      </Link>
    );
  }
  return (
    <button className={cn(base, "w-full text-left")} onClick={item.onClick ?? onClick}>
      <Icon className="size-4 shrink-0" />
      {item.label}
    </button>
  );
}

// ─── Sidebar (desktop) ───────────────────────────────────────────────────────

function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose?: () => void;
}) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tematicaOpen, setTematicaOpen] = React.useState(false);
  const [barrioOpen, setBarrioOpen] = React.useState(false);
  const userinfo: any = JSON.parse(localStorage.getItem("usuario") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("usuario");
    localStorage.removeItem("token");
    sessionStorage.clear();
    logout();
    onClose?.();
  };

  const isActive = (to?: string) => to === location.pathname;

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-[1150] flex flex-col transition-all duration-300 ease-in-out",
        "bg-gradient-to-b from-[#1a5f7a] to-[#134a5f]"
      )}
      style={{ width: open ? SIDEBAR_WIDTH : 0, overflow: "hidden" }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10 h-[64px]">
        <img src="/logo192.png" alt="Chicos X Baires" className="h-8 w-auto" />
      </div>

      {/* User */}
      {userinfo?.email && (
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-full bg-white/20 flex items-center justify-center">
              <User className="size-4 text-white/80" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-white/60 truncate">{userinfo.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
        <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
          Lugares
        </p>
        {[navItems[0], navItems[2]].map((item) => (
          <NavLink
            key={item.label}
            item={item}
            isActive={isActive(item.to)}
            onClick={onClose}
          />
        ))}

        {/* Temáticas collapsible */}
        <button
          onClick={() => setTematicaOpen((v) => !v)}
          className={cn(
            "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium w-full text-left transition-all",
            "text-white/75 hover:bg-white/10 hover:text-white"
          )}
        >
          <Layers className="size-4 shrink-0" />
          <span className="flex-1">Por Temática</span>
          {tematicaOpen ? (
            <ChevronDown className="size-3.5 text-white/50" />
          ) : (
            <ChevronRight className="size-3.5 text-white/50" />
          )}
        </button>
        {tematicaOpen && (
          <div className="flex flex-col gap-0.5 ml-1 shrink-0 h-48 overflow-y-auto">
            {tematicaItems.map((t) => (
              <NavLink
                key={t.to}
                item={t}
                isActive={isActive(t.to)}
                onClick={onClose}
                indent
              />
            ))}
          </div>
        )}

        {/* Barrios collapsible */}
        <button
          onClick={() => setBarrioOpen((v) => !v)}
          className={cn(
            "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium w-full text-left transition-all",
            "text-white/75 hover:bg-white/10 hover:text-white"
          )}
        >
          <Building2 className="size-4 shrink-0" />
          <span className="flex-1">Por Barrio</span>
          {barrioOpen ? (
            <ChevronDown className="size-3.5 text-white/50" />
          ) : (
            <ChevronRight className="size-3.5 text-white/50" />
          )}
        </button>
        {barrioOpen && (
          <div className="flex flex-col gap-0.5 ml-1 shrink-0 h-48 overflow-y-auto">
            {barrioItems.map((b) => (
              <NavLink
                key={b.to}
                item={b}
                isActive={isActive(b.to)}
                onClick={onClose}
                indent
              />
            ))}
          </div>
        )}

        <Separator className="bg-white/10 my-2" />
        <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
          Eventos
        </p>
        <NavLink
          item={navItems[3]}
          isActive={isActive(navItems[3].to)}
          onClick={onClose}
        />

        <Separator className="bg-white/10 my-2" />
        <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
          Planificador
        </p>
        <NavLink
          item={navItems[4]}
          isActive={isActive(navItems[4].to)}
          onClick={onClose}
        />

        <Separator className="bg-white/10 my-2" />
        <NavLink
          item={navItems[5]}
          isActive={isActive(navItems[5].to)}
          onClick={onClose}
        />
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-white/10 flex flex-col gap-1">
        {user ? (
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all w-full text-left"
          >
            <LogOut className="size-4 shrink-0" />
            Cerrar sesión
          </button>
        ) : (
          <Link
            to="/login"
            onClick={onClose}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all"
          >
            <LogIn className="size-4 shrink-0" />
            Iniciar sesión
          </Link>
        )}
        <button
          onClick={() =>
            window.open("https://www.instagram.com/chicosxbaires", "_blank")
          }
          className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all w-full text-left"
        >
          <InstagramIcon className="size-4 shrink-0" />
          Instagram
        </button>
        <Link
          to="/privacidad"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all"
        >
          <ShieldCheck className="size-4 shrink-0" />
          Avisos legales
        </Link>
      </div>
    </aside>
  );
}

// ─── Mobile Menu Sheet ────────────────────────────────────────────────────────

function MobileMenuSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [tematicaOpen, setTematicaOpen] = React.useState(false);
  const [barrioOpen, setBarrioOpen] = React.useState(false);
  const userinfo: any = JSON.parse(localStorage.getItem("usuario") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("usuario");
    localStorage.removeItem("token");
    sessionStorage.clear();
    logout();
    onClose();
  };

  const isActive = (to?: string) => to === location.pathname;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-[300px] p-0 bg-gradient-to-b from-[#1a5f7a] to-[#134a5f] border-none flex flex-col">
        <SheetHeader className="px-5 py-4 border-b border-white/10">
          <SheetTitle className="text-white flex items-center gap-2">
            <img src="/logo192.png" alt="Logo" className="h-7 w-auto" />
            Chicos X Baires
          </SheetTitle>
          {userinfo?.email && (
            <p className="text-xs text-white/60 mt-1">{userinfo.email}</p>
          )}
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
          <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
            Lugares
          </p>
          {[navItems[0], navItems[2]].map((item) => (
            <NavLink
              key={item.label}
              item={item}
              isActive={isActive(item.to)}
              onClick={onClose}
            />
          ))}

          <button
            onClick={() => setTematicaOpen((v) => !v)}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all w-full text-left"
          >
            <Layers className="size-4 shrink-0" />
            <span className="flex-1">Por Temática</span>
            {tematicaOpen ? (
              <ChevronDown className="size-3.5 text-white/50" />
            ) : (
              <ChevronRight className="size-3.5 text-white/50" />
            )}
          </button>
          {tematicaOpen && (
            <div className="flex flex-col gap-0.5 ml-1 shrink-0 h-48 overflow-y-auto">
              {tematicaItems.map((t) => (
                <NavLink
                  key={t.to}
                  item={t}
                  isActive={isActive(t.to)}
                  onClick={onClose}
                  indent
                />
              ))}
            </div>
          )}

          {/* Barrios collapsible */}
          <button
            onClick={() => setBarrioOpen((v) => !v)}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all w-full text-left"
          >
            <Building2 className="size-4 shrink-0" />
            <span className="flex-1">Por Barrio</span>
            {barrioOpen ? (
              <ChevronDown className="size-3.5 text-white/50" />
            ) : (
              <ChevronRight className="size-3.5 text-white/50" />
            )}
          </button>
          {barrioOpen && (
            <div className="flex flex-col gap-0.5 ml-1 shrink-0 h-48 overflow-y-auto">
              {barrioItems.map((b) => (
                <NavLink
                  key={b.to}
                  item={b}
                  isActive={isActive(b.to)}
                  onClick={onClose}
                  indent
                />
              ))}
            </div>
          )}

          <Separator className="bg-white/10 my-2" />
          <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
            Eventos
          </p>
          <NavLink item={navItems[3]} isActive={isActive(navItems[3].to)} onClick={onClose} />

          <Separator className="bg-white/10 my-2" />
          <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/40">
            Planificador
          </p>
          <NavLink item={navItems[4]} isActive={isActive(navItems[4].to)} onClick={onClose} />

          <Separator className="bg-white/10 my-2" />
          <NavLink item={navItems[5]} isActive={isActive(navItems[5].to)} onClick={onClose} />
        </nav>

        <div className="px-3 py-4 border-t border-white/10 flex flex-col gap-1">
          {user ? (
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all w-full text-left"
            >
              <LogOut className="size-4 shrink-0" />
              Cerrar sesión
            </button>
          ) : (
            <Link
              to="/login"
              onClick={onClose}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all"
            >
              <LogIn className="size-4 shrink-0" />
              Iniciar sesión
            </Link>
          )}
          <button
            onClick={() =>
              window.open("https://www.instagram.com/chicosxbaires", "_blank")
            }
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-all w-full text-left"
          >
            <InstagramIcon className="size-4 shrink-0" />
            Instagram
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Bottom Navigation (mobile) ───────────────────────────────────────────────

function BottomNav() {
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (to: string) => location.pathname === to;

  const handleLogout = () => {
    localStorage.removeItem("usuario");
    localStorage.removeItem("token");
    sessionStorage.clear();
    logout();
  };

  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#1a5f7a] shadow-nav pb-safe">
      <div className="flex items-stretch justify-around h-[60px]">
        {[
          { to: "/", icon: Home, label: "Inicio" },
          { to: "/lista", icon: List, label: "Lugares" },
          { to: "/eventos", icon: CalendarDays, label: "Eventos" },
          { to: "/plan-salida", icon: Route, label: "Plan" },
        ].map(({ to, icon: Icon, label }) => (
          <Link
            key={to}
            to={to}
            className={cn(
              "flex flex-col items-center justify-center gap-0.5 flex-1 text-xs font-medium transition-all",
              isActive(to)
                ? "text-white"
                : "text-white/55 hover:text-white/80"
            )}
          >
            <Icon
              className={cn(
                "size-5 transition-transform",
                isActive(to) && "scale-110"
              )}
            />
            <span className="text-[10px]">{label}</span>
          </Link>
        ))}
        {user ? (
          <button
            onClick={handleLogout}
            className="flex flex-col items-center justify-center gap-0.5 flex-1 text-xs font-medium text-white/55 hover:text-white/80 transition-all"
          >
            <LogOut className="size-5" />
            <span className="text-[10px]">Salir</span>
          </button>
        ) : (
          <Link
            to="/login"
            className={cn(
              "flex flex-col items-center justify-center gap-0.5 flex-1 text-xs font-medium transition-all",
              isActive("/login") ? "text-white" : "text-white/55 hover:text-white/80"
            )}
          >
            <User className="size-5" />
            <span className="text-[10px]">Ingresar</span>
          </Link>
        )}
      </div>
    </nav>
  );
}

// ─── Top AppBar ───────────────────────────────────────────────────────────────

function AppBar({
  sidebarOpen,
  onToggleSidebar,
  onOpenMenu,
  isMobile,
}: {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onOpenMenu: () => void;
  isMobile: boolean;
}) {
  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-20 h-[64px] flex items-center px-4 bg-[#1a5f7a] shadow-sm transition-all duration-300",
        isMobile ? "left-0" : `left-[${sidebarOpen ? SIDEBAR_WIDTH : 0}px]`
      )}
      style={{
        left: isMobile ? 0 : sidebarOpen ? SIDEBAR_WIDTH : 0,
      }}
    >
      {!isMobile && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleSidebar}
          className="text-white hover:bg-white/10 mr-3"
          aria-label="Toggle sidebar"
        >
          <Menu className="size-5" />
        </Button>
      )}

      {(isMobile || !sidebarOpen) && (
        <Link to="/" className={cn("flex items-center", isMobile && "bg-white/15 rounded-xl px-2 py-1")}>
          <img
            src="/logo192.png"
            alt="Chicos X Baires"
            className="h-7 w-auto"
          />
        </Link>
      )}

      <div className="flex-1" />

      {isMobile && (
        <button
          onClick={onOpenMenu}
          className="flex items-center gap-1 text-white hover:bg-white/10 rounded-md px-2 py-1.5 text-xs font-medium transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="size-4" />
          <span>Menú</span>
        </button>
      )}
    </header>
  );
}

// ─── Main Layout ──────────────────────────────────────────────────────────────

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      setSidebarOpen(!e.matches);
    };
    setIsMobile(mq.matches);
    setSidebarOpen(!mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      {!isMobile && (
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile menu sheet */}
      {isMobile && (
        <MobileMenuSheet
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div
        className="flex-1 flex flex-col min-h-screen transition-all duration-300 overflow-x-hidden min-w-0"
        style={{
          marginLeft: !isMobile && sidebarOpen ? SIDEBAR_WIDTH : 0,
        }}
      >
        <AppBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
          onOpenMenu={() => setMobileMenuOpen(true)}
          isMobile={isMobile}
        />

        <main
          className={cn(
            "flex-1 pt-[64px] overflow-x-hidden",
            isMobile ? "pb-[60px]" : "pb-0"
          )}
          style={isMobile ? { paddingBottom: 'calc(60px + env(safe-area-inset-bottom))' } : undefined}
        >
          <AppRouter />
        </main>
      </div>

      {/* Mobile bottom nav */}
      {isMobile && <BottomNav />}

      {/* Floating Instagram CTA - mobile only */}
      {isMobile && (
        <a
          href="https://www.instagram.com/chicosxbaires"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-[72px] left-4 z-30 flex items-center gap-2 bg-gradient-to-r from-[#f09433] via-[#e6683c] to-[#dc2743] text-white text-xs font-semibold px-3 py-2 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
          aria-label="Seguinos en Instagram"
        >
          <InstagramIcon className="size-4 shrink-0" />
          Seguinos
        </a>
      )}
    </div>
  );
}
